import { useEffect, useRef } from "react";

/**
 * usePolling
 * Runs `fn` on an interval, but ONLY when the tab is visible.
 * Pauses automatically when tab goes to background.
 * Fires an immediate refresh when the user comes back to the tab.
 * Cleans up on unmount — no memory leaks.
 *
 * @param {Function} fn        async function to call each tick
 * @param {number}   interval  ms between calls (default 10000)
 * @param {boolean}  enabled   set false to pause (e.g. before first load)
 */
export function usePolling(fn, interval = 10000, enabled = true) {
  // Always hold the latest fn without restarting the interval
  const fnRef = useRef(fn);
  useEffect(() => { fnRef.current = fn; }, [fn]);

  useEffect(() => {
    if (!enabled) return;

    let timerId = null;

    const tick = () => {
      if (document.visibilityState === "visible") {
        fnRef.current();
      }
    };

    const start = () => { timerId = setInterval(tick, interval); };
    const stop  = () => { if (timerId) { clearInterval(timerId); timerId = null; } };

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        fnRef.current(); // immediate refresh on tab return
        start();
      } else {
        stop(); // pause while hidden — no wasted requests
      }
    };

    start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [interval, enabled]);
}
